"use client";

import { useEffect, useRef, useState } from "react";
import {
  Maximize,
  Pause,
  Play,
  Volume2,
  VolumeX,
} from "lucide-react";

import { youtubeThumbnailUrl } from "@/lib/feed/link-preview";
import { cn } from "@/lib/utils";

type YTPlayer = {
  playVideo: () => void;
  pauseVideo: () => void;
  seekTo: (seconds: number, allowSeekAhead: boolean) => void;
  getCurrentTime: () => number;
  getDuration: () => number;
  mute: () => void;
  unMute: () => void;
  isMuted: () => boolean;
  destroy: () => void;
};

type YTNamespace = {
  Player: new (
    element: HTMLElement,
    options: {
      videoId: string;
      width?: string | number;
      height?: string | number;
      playerVars?: Record<string, string | number>;
      events?: {
        onReady?: (event: { target: YTPlayer }) => void;
        onStateChange?: (event: { data: number; target: YTPlayer }) => void;
      };
    },
  ) => YTPlayer;
  PlayerState: {
    ENDED: number;
    PLAYING: number;
    PAUSED: number;
  };
};

declare global {
  interface Window {
    YT?: YTNamespace;
    onYouTubeIframeAPIReady?: () => void;
  }
}

let apiLoader: Promise<YTNamespace> | null = null;

function loadYouTubeApi() {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("YouTube API is browser-only."));
  }
  if (window.YT?.Player) {
    return Promise.resolve(window.YT);
  }
  if (apiLoader) {
    return apiLoader;
  }

  apiLoader = new Promise((resolve, reject) => {
    const startedAt = Date.now();
    const previous = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      previous?.();
    };

    if (!document.querySelector("script[src='https://www.youtube.com/iframe_api']")) {
      const script = document.createElement("script");
      script.src = "https://www.youtube.com/iframe_api";
      script.async = true;
      document.head.appendChild(script);
    }

    const tick = () => {
      if (window.YT?.Player) {
        resolve(window.YT);
        return;
      }
      if (Date.now() - startedAt > 15000) {
        apiLoader = null;
        reject(new Error("A YouTube lejátszó nem töltődött be."));
        return;
      }
      window.setTimeout(tick, 50);
    };
    tick();
  });

  return apiLoader;
}

function fitYouTubeIframe(wrapper: HTMLElement) {
  const iframe = wrapper.querySelector("iframe");
  if (!iframe) {
    return;
  }
  iframe.setAttribute("width", "100%");
  iframe.setAttribute("height", "100%");
  iframe.style.cssText =
    "position:absolute;inset:0;width:100%;height:100%;border:0;pointer-events:none;";
}

function formatTime(seconds: number) {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return "0:00";
  }
  const total = Math.floor(seconds);
  const mins = Math.floor(total / 60);
  const secs = String(total % 60).padStart(2, "0");
  return `${mins}:${secs}`;
}

export function YouTubePlayer({
  videoId,
  title,
  thumbnail,
}: {
  videoId: string;
  title: string;
  thumbnail?: string | null;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<YTPlayer | null>(null);
  const createdRef = useRef(false);
  const playingRef = useRef(false);
  const hideTimer = useRef<number>(0);
  const [started, setStarted] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [chromeVisible, setChromeVisible] = useState(true);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  playingRef.current = playing;

  function revealChrome(keep = false) {
    setChromeVisible(true);
    window.clearTimeout(hideTimer.current);
    if (keep) {
      return;
    }
    hideTimer.current = window.setTimeout(() => {
      if (playingRef.current) {
        setChromeVisible(false);
      }
    }, 1800);
  }

  function createPlayer(YT: YTNamespace, wrapper: HTMLElement) {
    if (createdRef.current) {
      return;
    }
    createdRef.current = true;
    const target = document.createElement("div");
    target.style.cssText = "width:100%;height:100%;";
    wrapper.replaceChildren(target);

    const player = new YT.Player(target, {
      videoId,
      width: "100%",
      height: "100%",
      playerVars: {
            autoplay: 1,
            cc_load_policy: 0,
            controls: 0,
            disablekb: 1,
            fs: 0,
            iv_load_policy: 3,
            modestbranding: 1,
            playsinline: 1,
            rel: 0,
            origin: window.location.origin,
      },
      events: {
        onReady: (event) => {
          fitYouTubeIframe(wrapper);
          event.target.playVideo();
          setDuration(event.target.getDuration() || 0);
          setMuted(event.target.isMuted());
          revealChrome();
        },
        onStateChange: (event) => {
          fitYouTubeIframe(wrapper);
          const isPlaying = event.data === YT.PlayerState.PLAYING;
          const ended = event.data === YT.PlayerState.ENDED;
          setPlaying(isPlaying);
          setDuration(event.target.getDuration() || 0);
          if (ended) {
            setCurrentTime(event.target.getDuration() || 0);
            revealChrome(true);
            return;
          }
          if (isPlaying) {
            revealChrome();
          } else {
            revealChrome(true);
          }
        },
      },
    });
    playerRef.current = player;
    window.setTimeout(() => fitYouTubeIframe(wrapper), 0);
  }

  useEffect(() => {
    void loadYouTubeApi();
  }, []);

  useEffect(() => {
    if (!started || createdRef.current || !wrapperRef.current) {
      return;
    }

    let cancelled = false;
    void loadYouTubeApi()
      .then((YT) => {
        if (!cancelled && wrapperRef.current) {
          createPlayer(YT, wrapperRef.current);
        }
      })
      .catch(() => {
        if (!cancelled) {
          createdRef.current = false;
          setStarted(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [started, videoId]);

  useEffect(() => {
    return () => {
      window.clearTimeout(hideTimer.current);
      playerRef.current?.destroy();
      playerRef.current = null;
      createdRef.current = false;
    };
  }, [videoId]);

  useEffect(() => {
    if (!started || !playing) {
      return;
    }
    const timer = window.setInterval(() => {
      const player = playerRef.current;
      if (!player) {
        return;
      }
      setCurrentTime(player.getCurrentTime() || 0);
    }, 250);
    return () => window.clearInterval(timer);
  }, [playing, started]);

  function startPlayback() {
    setStarted(true);
    const wrapper = wrapperRef.current;
    const YT = window.YT;
    if (wrapper && YT?.Player) {
      createPlayer(YT, wrapper);
    }
  }

  function togglePlay(event?: { stopPropagation(): void }) {
    event?.stopPropagation();
    const player = playerRef.current;
    if (!player) {
      return;
    }
    if (playing) {
      player.pauseVideo();
    } else {
      player.playVideo();
    }
  }

  function toggleMute(event: { stopPropagation(): void }) {
    event.stopPropagation();
    const player = playerRef.current;
    if (!player) {
      return;
    }
    if (player.isMuted()) {
      player.unMute();
      setMuted(false);
    } else {
      player.mute();
      setMuted(true);
    }
    revealChrome();
  }

  function seek(value: number) {
    playerRef.current?.seekTo(value, true);
    setCurrentTime(value);
    revealChrome();
  }

  async function toggleFullscreen(event: { stopPropagation(): void }) {
    event.stopPropagation();
    const root = rootRef.current;
    if (!root) {
      return;
    }
    if (document.fullscreenElement) {
      await document.exitFullscreen();
    } else {
      await root.requestFullscreen();
    }
    revealChrome();
  }

  const poster = thumbnail || youtubeThumbnailUrl(videoId);

  return (
    <div
      ref={rootRef}
      className="relative aspect-video overflow-hidden bg-black"
    >
      <div
        ref={wrapperRef}
        className="pointer-events-none absolute inset-0"
      />
      {!started ? (
        <button
          type="button"
          className="absolute inset-0 z-[1] block"
          aria-label="YouTube videó lejátszása"
          onClick={startPlayback}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={poster} alt="" className="h-full w-full object-cover" />
          <span className="absolute inset-0 bg-black/30" />
          <span className="absolute top-1/2 left-1/2 flex size-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-red-600 text-white shadow-lg">
            <Play className="ml-0.5 size-7 fill-current" />
          </span>
        </button>
      ) : (
        <div
          className="absolute inset-0 z-[1]"
          onMouseMove={() => revealChrome(!playing)}
          onMouseLeave={() => {
            if (playing) {
              setChromeVisible(false);
            }
          }}
          onClick={() => togglePlay()}
        >
          <div
            className={cn(
              "pointer-events-none absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-transparent transition-opacity",
              chromeVisible ? "opacity-100" : "opacity-0",
            )}
          />
          <div
            className={cn(
              "pointer-events-none absolute top-1/2 left-1/2 flex size-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-white transition-opacity",
              chromeVisible && !playing ? "opacity-100" : "opacity-0",
            )}
          >
            <Play className="ml-0.5 size-6 fill-current" />
          </div>
          <div
            className={cn(
              "absolute inset-x-0 bottom-0 space-y-1.5 px-3 pt-8 pb-2.5 transition-opacity",
              chromeVisible
                ? "pointer-events-auto opacity-100"
                : "pointer-events-none opacity-0",
            )}
            onClick={(event) => event.stopPropagation()}
          >
            <input
              type="range"
              min={0}
              max={Math.max(duration, 1)}
              step={0.1}
              value={Math.min(currentTime, duration)}
              aria-label="Lejátszási pozíció"
              className="h-1 w-full cursor-pointer appearance-none rounded-full bg-white/30 accent-red-600"
              onChange={(event) => seek(Number(event.target.value))}
            />
            <div className="flex items-center gap-2 text-white">
              <button
                type="button"
                className="rounded-md p-1 hover:bg-white/10"
                aria-label={playing ? "Szünet" : "Lejátszás"}
                onClick={togglePlay}
              >
                {playing ? (
                  <Pause className="size-4 fill-current" />
                ) : (
                  <Play className="size-4 fill-current" />
                )}
              </button>
              <p className="text-[11px] tabular-nums text-white/90">
                {formatTime(currentTime)} / {formatTime(duration)}
              </p>
              <button
                type="button"
                className="ml-auto rounded-md p-1 hover:bg-white/10"
                aria-label={muted ? "Hang be" : "Némítás"}
                onClick={toggleMute}
              >
                {muted ? (
                  <VolumeX className="size-4" />
                ) : (
                  <Volume2 className="size-4" />
                )}
              </button>
              <button
                type="button"
                className="rounded-md p-1 hover:bg-white/10"
                aria-label="Teljes képernyő"
                onClick={(event) => void toggleFullscreen(event)}
              >
                <Maximize className="size-4" />
              </button>
            </div>
          </div>
        </div>
      )}
      <span className="sr-only">{title}</span>
    </div>
  );
}
